import {
  ArrowBack,
  CheckCircle,
  LinkedIn,
  LocationOn
} from "@mui/icons-material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import KeyboardDoubleArrowUpRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowUpRounded";
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { OverlayLoader } from "components/Common/Loader/loaderWithOverlay";
import OuterDiv from "components/Common/OuterDivComponent";
import React from "react";
import { toast } from "react-toastify";
import { campaignManagementService } from "services/campaign";
import { DEFAULT_TOKEN } from "utilities/constants";
import FeedBackAndSummary from "./FeedBackAndSummary";
import { AIConversation } from "./AIConversation";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

interface LeadDetailsProps {
  candidateData: {
    priority_list: boolean;
    username: string;
    profileDetails: any;
    evaluationDetails: any;
    jobId: string;
    expertId: string;
    campaignId: string;
    black_list: boolean;
    lead_info?: {
      lead_id: string;
      lead_name: string;
    };
  };
  onClose: () => void;
  openFeedBackModal: () => void;
  setSelectedUserName: (username: string) => void;
  onActionPerformed?: () => void;
}


const ActionButton = styled(Button)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "4px",
  padding: "4px 8px",
  border: "1px solid #D0D5DD",
  backgroundColor: "#FAFAFA",
  color: "#101828",
  fontWeight: 600,
  fontSize: "12px",
  textTransform: "none",
  boxShadow: "none",
  lineHeight: 1.5,
  minWidth: "32px",
  height: "28px",
  '&:hover': {
    backgroundColor: "#F0F0F0",
    borderColor: "#C0C5CC",
  },
}));

const ActionIconButton = styled(IconButton)(({ theme }) => ({
  padding: "4px",
  borderRadius: "4px",
  '&:hover': {
    backgroundColor: "#F5F5F5",
  },
}));

const ShortlistedChip = styled(Chip)({
  fontWeight: 600,
  borderRadius: "4px",
  height: "24px",
  border: "1px solid #039855",
  color: "#027A48",
  background: "transparent",
  fontSize: "11px",
});

const LeadDetails = ({
  candidateData,
  onClose,
  openFeedBackModal,
  setSelectedUserName,
  onActionPerformed
}: LeadDetailsProps): JSX.Element => {
  const [tabValue, setTabValue] = React.useState(0);
  const profile = candidateData?.profileDetails;
  const [loading, setLoading] = React.useState<boolean>(false);
  const [localLoader, setLocalLoader] = React.useState<boolean>(false);



  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!profile) return <></>;

  const skills = profile.skills || [];

  const currentCompany = profile.current_company || {};

  const handleAction = async (
    type: "blacklist" | "whitelist" | "prioritize"
  ) => {
    try {
      setLocalLoader(true);
      const payload = {
        token: DEFAULT_TOKEN,
        expertId: candidateData.expertId,
        jobId: candidateData.jobId,
        campaignId: candidateData.campaignId,
        userNames: candidateData.username,
      };

      console.log("Action payload:", payload);
      
      if (!payload.expertId) {
        toast.error("Expert ID is missing. Cannot perform this action.");
        return;
      }
      
      if (!payload.jobId) {
        toast.error("Job ID is missing. Cannot perform this action.");
        return;
      }
      
      if (!payload.campaignId) {
        toast.error("Campaign ID is missing. Cannot perform this action.");
        return;
      }
  
      if (type === "blacklist") {
        await campaignManagementService.updateBlackListCandidates({
          ...payload,
          whiteList: false,
        });
        candidateData.black_list = true;
        toast.success("Candidate blacklisted.");
        if (onActionPerformed) onActionPerformed();
      } else if (type === "whitelist") {
        await campaignManagementService.updateBlackListCandidates({
          ...payload,
          whiteList: true,
        });
        candidateData.black_list = false;
        toast.success("Candidate whitelisted.");
        if (onActionPerformed) onActionPerformed();
      } else if (type === "prioritize") {
        await campaignManagementService.PrioritizeLeads({
          ...payload,
          actionType: "ADD"
        });
        candidateData.priority_list = true;
        toast.success("Candidate prioritized.");
        if (onActionPerformed) onActionPerformed();
      }
    } catch (error: any) {
      console.error("Action error:", error);
      toast.error(error?.response?.data?.message || "Action failed.");
    } finally {
      setLocalLoader(false);
    }
  };
  

  const handleFeedback = () => {
    setSelectedUserName(candidateData?.username);
    openFeedBackModal();
    if (onActionPerformed) onActionPerformed();
  };

  if (loading) {
    return <OverlayLoader loading={true} />;
  }

  return (
    <OuterDiv style={{ marginLeft: 0, width: "100%", top: 0 }}>
      <OverlayLoader loading={localLoader} />
      <Box sx={{ paddingBottom: "10px" }}>
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            background: "#fff",
          }}
        >
          <IconButton size="small" sx={{ mr: 1 }} onClick={()=> {
            onClose();
          }}>
            <ArrowBack fontSize="small" />
          </IconButton>

          <Box sx={{ display: "flex", flexDirection: "column" , justifyContent: "space-between"}}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 4,  }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {profile.position}
                </Typography>
              </Box>

              <Box>
                {candidateData?.lead_info?.lead_id && candidateData?.lead_info?.lead_name && (
                  <ShortlistedChip
                    label="Shortlisted"
                    size="small"
                  />
                )}
              </Box>
            </Box>

            

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                whiteSpace: "nowrap",
              }}
            >
              {profile?.city}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 16px 0px 16px",
          }}
        >
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {!candidateData?.lead_info?.lead_id && (
              <>
                {!candidateData?.black_list ? (
                  <>
                    <ActionButton
                      variant="outlined"
                      size="small"
                      onClick={() => handleAction("blacklist")}
                    >
                      <Tooltip title={"Blacklist this Candidate"} arrow><ReportProblemRoundedIcon fontSize="small" /></Tooltip>
                    </ActionButton>

                    <ActionButton
                      variant="outlined"
                      size="small"
                      onClick={() => handleAction("prioritize")}
                    >
                      <Tooltip title={"Prioritize this Candidate"} arrow><KeyboardDoubleArrowUpRoundedIcon fontSize="small" /></Tooltip>
                    </ActionButton>
                  </>
                ) : (
                  <ActionButton
                    variant="outlined"
                    size="small"
                    onClick={() => handleAction("whitelist")}
                  >
                    <Tooltip title={"Whitelist this Candidate"} arrow><CheckCircle fontSize="small" /></Tooltip>
                  </ActionButton>
                )}

            
            <ActionButton size="small" onClick={() => handleFeedback()}>
              <Tooltip title={"Feedback"} arrow><ChatBubbleOutlineRoundedIcon fontSize="small" /></Tooltip>
            </ActionButton>
            </>
     )}
          </Box>
          {/* <Box>
            {candidateData?.lead_info?.lead_id && candidateData?.lead_info?.lead_name && (
              <Chip
                label="Shortlisted"
                size="small"
                sx={{
                  fontWeight: 600,
                  borderRadius: "4px",
                  height: "24px",
                  border: "1px solid #039855 ",
                  color: " #027A48",
                  background:"transparent",
                  fontSize: "11px",
                }}
              />
            )}
          </Box> */}
        </Box>

        <Box sx={{ p: 2 }}>
          <Card sx={{ mb: 2, border: "1px solid #D9DDE3" }}>
            <CardContent
              sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
            >
              <Avatar
                sx={{ width: 48, height: 48 }}
                alt={profile.name}
                src={profile.avatar || "/api/placeholder/48/48"}
              />
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box>
                    <Typography variant="h6" component="div">
                      {profile.name} | {profile.position} {" "}
                      {candidateData?.black_list && (<Tooltip title={"BlackListed Candidate"} arrow ><NoAccountsIcon fontSize="small" /></Tooltip>)}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {profile.city || profile.location}
                      </Typography>
                      <Divider orientation="vertical" flexItem />
                      <Link
                        href={profile.profile_url}
                        target="_blank"
                        rel="noopener"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          textDecoration: "none",
                          color: "text.secondary",
                        }}
                      >
                        <LinkedIn fontSize="small" />
                        {profile.profile_url}
                      </Link>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    {candidateData?.evaluationDetails?.final_score && (
                      <Box
                        sx={{ position: "relative", display: "inline-flex" }}
                      >
                        <CircularProgress
                          variant="determinate"
                          value={parseFloat(
                            candidateData?.evaluationDetails?.final_score
                          )}
                          size={64}
                          thickness={4}
                          sx={{ color: "#4caf50" }}
                        />
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: "absolute",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            component="div"
                            color="text.secondary"
                            sx={{ fontWeight: "bold", fontSize: "12px" }}
                          >
                            { candidateData && candidateData?.evaluationDetails?.final_score }%
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  {/* <Chip
label={profile.fitScore || "High Fit"}
size="small"
sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }}
/>
{profile.tags && profile.tags.map((tag: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined, index: React.Key | null | undefined) => (
<Chip key={index} label={tag} size="small" />
))}
<Chip
icon={<Add />}
label="Add Tag"
size="small"
variant="outlined"
/> */}
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ position: "relative" }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              textColor="primary"
              TabIndicatorProps={{
                sx: {
                  backgroundColor: "#fff",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  height: "100%",
                  zIndex: 0,
                },
              }}
              sx={{
                "& .MuiTab-root": {
                  zIndex: 1,
                  position: "relative",
                },
                "& .Mui-selected": {
                  fontWeight: "bold",
                  color: "text.secondary",
                },
              }}
            >
              <Tab label="Candidate Information" />
              <Tab label="AI Summary & Feedback" />
              <Tab label="AI Conversation" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Box
              sx={{
                background: "#fff",
                p: 2,
                borderRadius: "6px",
                mb: 3,
                border: "1px solid #D9DDE3",
              }}
            >
              {candidateData?.evaluationDetails?.recommendation && (
                <Alert
                  severity={
                    candidateData?.evaluationDetails?.recommendation.toLowerCase().includes(
                      "not recommended"
                    )
                      ? "error"
                      : "success"
                  }
                  icon={<CheckCircle />}
                  sx={{
                    mb: 3,
                    ...(candidateData?.evaluationDetails?.recommendation.includes(
                      "Not Recommended"
                    ) && { background: "#f8d7da", color: "#721c24" }),
                  }}
                >
                  {candidateData?.evaluationDetails?.recommendation}
                </Alert>
              )}

              <Box sx={{ mb: 4, borderBottom: 2, borderColor: "divider" }}>
                {(currentCompany?.title || profile.position) && (
                  <Box
                    sx={{
                      display: "flex",
                      py: 1.5,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="#333333"
                      sx={{
                        width: "200px",
                        flexShrink: 0,
                      }}
                    >
                      Current Role
                    </Typography>
                    <Typography color="#656E81">
                      {currentCompany?.title
                        ? `${currentCompany.title} at ${currentCompany.name}`
                        : profile.position}
                    </Typography>
                  </Box>
                )}

                {(profile.city || profile.location) && (
                  <Box
                    sx={{
                      display: "flex",
                      py: 1.5,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="#333333"
                      sx={{
                        width: "200px",
                        flexShrink: 0,
                      }}
                    >
                      Location
                    </Typography>
                    <Typography color="#656E81">
                      {profile.city || profile.location}
                    </Typography>
                  </Box>
                )}

                {profile.profile_url && (
                  <Box
                    sx={{
                      display: "flex",
                      py: 1.5,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="#333333"
                      sx={{
                        width: "200px",
                        flexShrink: 0,
                      }}
                    >
                      LinkedIn Profile
                    </Typography>
                    <Link
                      href={profile.profile_url}
                      target="_blank"
                      rel="noopener"
                      sx={{
                        color: "#656E81",
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {profile.profile_url}
                    </Link>
                  </Box>
                )}

                {profile.followers && (
                  <Box
                    sx={{
                      display: "flex",
                      py: 1.5,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="#333333"
                      sx={{
                        width: "200px",
                        flexShrink: 0,
                      }}
                    >
                      Followers
                    </Typography>
                    <Typography color="#656E81">{profile.followers}</Typography>
                  </Box>
                )}

                {profile.connections && (
                  <Box
                    sx={{
                      display: "flex",
                      py: 1.5,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="#333333"
                      sx={{
                        width: "200px",
                        flexShrink: 0,
                      }}
                    >
                      Connections
                    </Typography>
                    <Typography color="#656E81">
                      {profile.connections}
                    </Typography>
                  </Box>
                )}
              </Box>

              {(profile.about || profile.profileSnippet) && (
                <Box
                  sx={{
                    mb: 6,
                    borderBottom: 2,
                    borderColor: "divider",
                    paddingBottom: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: "text.secondary", fontWeight: "600" }}
                    gutterBottom
                  >
                    Profile Snippet
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {profile.about || profile.profileSnippet}
                  </Typography>
                </Box>
              )}

              {skills.length > 0 && (
                <Box
                  sx={{
                    mb: 4,
                    borderBottom: 2,
                    borderColor: "divider",
                    paddingBottom: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: "text.secondary", fontWeight: "600" }}
                    gutterBottom
                  >
                    Skills
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {skills.map(
                      (
                        skill:
                          | string
                          | number
                          | boolean
                          | React.ReactElement<
                              any,
                              string | React.JSXElementConstructor<any>
                            >
                          | Iterable<React.ReactNode>
                          | React.ReactPortal
                          | null
                          | undefined,
                        index: React.Key | null | undefined
                      ) => (
                        <Chip
                          key={index}
                          label={skill}
                          size="small"
                          sx={{ borderRadius: "4px" }}
                        />
                      )
                    )}
                  </Box>
                </Box>
              )}

              {profile.experience && profile.experience.length > 0 && (
                <Box sx={{ mb: 5 }}>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    fontWeight="600"
                    gutterBottom
                  >
                    Work History
                  </Typography>

                  {profile.experience.map(
                    (
                      exp: {
                        company_logo_url: any;
                        company:
                          | string
                          | number
                          | boolean
                          | React.ReactElement<
                              any,
                              string | React.JSXElementConstructor<any>
                            >
                          | Iterable<React.ReactNode>
                          | null
                          | undefined;
                        type:
                          | string
                          | number
                          | boolean
                          | React.ReactElement<
                              any,
                              string | React.JSXElementConstructor<any>
                            >
                          | Iterable<React.ReactNode>
                          | React.ReactPortal
                          | null
                          | undefined;
                        title:
                          | string
                          | number
                          | boolean
                          | React.ReactElement<
                              any,
                              string | React.JSXElementConstructor<any>
                            >
                          | Iterable<React.ReactNode>
                          | React.ReactPortal
                          | null
                          | undefined;
                        start_date:
                          | string
                          | number
                          | boolean
                          | React.ReactElement<
                              any,
                              string | React.JSXElementConstructor<any>
                            >
                          | Iterable<React.ReactNode>
                          | React.ReactPortal
                          | null
                          | undefined;
                        end_date: any;
                        duration: any;
                        location:
                          | string
                          | number
                          | boolean
                          | React.ReactElement<
                              any,
                              string | React.JSXElementConstructor<any>
                            >
                          | Iterable<React.ReactNode>
                          | React.ReactPortal
                          | null
                          | undefined;
                        description:
                          | string
                          | number
                          | boolean
                          | React.ReactElement<
                              any,
                              string | React.JSXElementConstructor<any>
                            >
                          | Iterable<React.ReactNode>
                          | React.ReactPortal
                          | null
                          | undefined;
                        achievements: any[];
                      },
                      index: React.Key | null | undefined
                    ) => (
                      <Box key={index}>
                        <Box
                          sx={{ display: "flex", gap: 2, alignItems: "center" }}
                        >
                          <Avatar
                            src={exp.company_logo_url || "/api/placeholder/40/40"}
                            alt={exp.company as string}
                            variant="rounded"
                            sx={{ width: 32, height: 32 }}
                          />
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {exp.company}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mt: 0.5,
                              }}
                            >
                              {exp.type && (
                                <Chip
                                  label={exp.type}
                                  size="small"
                                  sx={{ fontSize: "10px", height: 20 }}
                                />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {exp.title} • {exp.start_date} -{" "}
                              {exp.end_date || ""}
                              {exp.duration && ` • ${exp.duration}`}
                            </Typography>
                            {exp.location && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {exp.location}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {exp.description && (
                          <Typography
                            variant="body2"
                            sx={{ mt: 1, pl: 6 }}
                            color="text.secondary"
                          >
                            {exp.description}
                          </Typography>
                        )}

                        {exp.achievements && (
                          <List dense sx={{ pl: 6, pt: 1 }}>
                            {exp.achievements.map(
                              (
                                achievement: any,
                                i: React.Key | null | undefined
                              ) => (
                                <ListItem key={i} disablePadding>
                                  <ListItemText
                                    primary={`• ${achievement}`}
                                    primaryTypographyProps={{
                                      variant: "body2",
                                    }}
                                  />
                                </ListItem>
                              )
                            )}
                          </List>
                        )}
                        {typeof index === "number" &&
                          index < profile.experience.length - 1 && (
                            <Divider sx={{ mt: 2, mb: 3 }} />
                          )}
                      </Box>
                    )
                  )}
                </Box>
              )}

              {profile.education && profile.education.length > 0 && (
                <Box>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    fontWeight="600"
                    gutterBottom
                  >
                    Education
                  </Typography>
                  {profile.education.map(
                    (
                      edu: {
                        institute_logo_url: any;
                        institution:
                          | string
                          | number
                          | boolean
                          | React.ReactElement<
                              any,
                              string | React.JSXElementConstructor<any>
                            >
                          | Iterable<React.ReactNode>
                          | null
                          | undefined;
                        degree:
                          | string
                          | number
                          | boolean
                          | React.ReactElement<
                              any,
                              string | React.JSXElementConstructor<any>
                            >
                          | Iterable<React.ReactNode>
                          | React.ReactPortal
                          | null
                          | undefined;
                        start_date:
                          | string
                          | number
                          | boolean
                          | React.ReactElement<
                              any,
                              string | React.JSXElementConstructor<any>
                            >
                          | Iterable<React.ReactNode>
                          | React.ReactPortal
                          | null
                          | undefined;
                        end_date: any;
                        location: any;
                      },
                      index: React.Key | null | undefined
                    ) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "center",
                          mb: 3,
                        }}
                      >
                        <Avatar
                          src={edu.institute_logo_url || "/api/placeholder/40/40"}
                          variant="rounded"
                          sx={{ width: 32, height: 32 }}
                        />
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {edu.institution}
                          </Typography>
                          <Typography variant="body2">{edu.degree}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {edu.start_date} - {edu.end_date || "Present"}
                            {edu.location && ` • ${edu.location}`}
                          </Typography>
                        </Box>
                      </Box>
                    )
                  )}
                </Box>
              )}

              {profile.certifications && profile.certifications.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    Certifications
                  </Typography>
                  <List dense>
                    {profile.certifications.map(
                      (
                        cert: {
                          name:
                            | string
                            | number
                            | boolean
                            | React.ReactElement<
                                any,
                                string | React.JSXElementConstructor<any>
                              >
                            | Iterable<React.ReactNode>
                            | React.ReactPortal
                            | null
                            | undefined;
                          details:
                            | string
                            | number
                            | boolean
                            | React.ReactElement<
                                any,
                                string | React.JSXElementConstructor<any>
                              >
                            | Iterable<React.ReactNode>
                            | React.ReactPortal
                            | null
                            | undefined;
                        },
                        index: React.Key | null | undefined
                      ) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={`• ${cert.name}`}
                            secondary={cert.details}
                            primaryTypographyProps={{ fontWeight: 500 }}
                          />
                        </ListItem>
                      )
                    )}
                  </List>
                </Box>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box
              sx={{
                background: "#fff",
                p: 2,
                borderRadius: "6px",
                mb: 3,
                border: "1px solid #D9DDE3",
              }}
            >
              <FeedBackAndSummary
                evaluationDetails={candidateData.evaluationDetails}
              />
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <AIConversation
                expertId={candidateData.expertId}
                jobId={candidateData.jobId}
                campaignId={candidateData.campaignId}
                username={candidateData.username}
              />
            </Box>
          </TabPanel>
        </Box>
      </Box>
    </OuterDiv>
  );
};

export default LeadDetails;
